import React, { useState, useMemo } from 'react';
import { Table, Input, Button, Space, Tag, Row, Col, Popconfirm, Modal, Tooltip } from 'antd';
import { SearchOutlined, EditOutlined, DeleteOutlined, CopyOutlined, LockOutlined, BranchesOutlined, MessageOutlined, ExclamationCircleFilled } from '@ant-design/icons';
import UserAvatar from '../common/UserAvatar';

const defaultTemplateIds = [
    '0e23c61f-f49b-40c9-bf8f-7f08d9406b24',
    '2f3c8d23-50bb-4dc3-8cc6-843c9dd4a74a',
    'a8d792a9-04c4-472d-8083-80053a4a934c',
];

const MyTemplates = ({
    items,
    loading,
    userData,
    isAdmin,
    onEditTemplate,
    onEditSequence,
    onDeleteTemplate,
    onDuplicateTemplate,
    onFetchEnrollmentCount,
}) => {
    const [search, setSearch] = useState('');

    const filteredItems = useMemo(() => {
        if (!search) return items;
        const s = search.toLowerCase();
        return items.filter(item => {
            const title = (item.title || '').toLowerCase();
            const msg = (item.message || '').toLowerCase();
            const cat = (item.category || '').toLowerCase();
            const owner = (item.owner?.name || '').toLowerCase();
            return title.includes(s) || msg.includes(s) || cat.includes(s) || owner.includes(s);
        });
    }, [items, search]);

    const columns = [
        {
            title: 'Type',
            dataIndex: 'type',
            width: 110,
            filters: [
                { text: 'Message', value: 'single-step' },
                { text: 'Sequence', value: 'multi-step' },
            ],
            onFilter: (value, record) => record.type === value,
            render: (type) => type === 'multi-step'
                ? <Tag icon={<BranchesOutlined />} color="purple" style={{ margin: 0, borderRadius: 999 }}>Sequence</Tag>
                : <Tag icon={<MessageOutlined />} color="blue" style={{ margin: 0, borderRadius: 999 }}>Message</Tag>,
        },
        {
            title: 'Template Name',
            dataIndex: 'title',
            sorter: (a, b) => (a.title || '').localeCompare(b.title || ''),
            render: (text, record) => {
                const tokens = record.invalidMergeTokens || [];
                if (!tokens.length) return text;
                const shown = tokens.slice(0, 5).join(', ');
                const overflow = tokens.length > 5 ? ` (+${tokens.length - 5} more)` : '';
                return (
                    <Space size={8} wrap>
                        <span>{text}</span>
                        <Tooltip title={`Unresolved merge field${tokens.length > 1 ? 's' : ''}: ${shown}${overflow}`}>
                            <Tag
                                color="error"
                                icon={<ExclamationCircleFilled />}
                                style={{ margin: 0, borderRadius: 999 }}
                            >
                                Action required
                            </Tag>
                        </Tooltip>
                    </Space>
                );
            },
        },
        {
            title: 'Message',
            dataIndex: 'message',
            ellipsis: true,
            sorter: (a, b) => (a.message || a.description || '').localeCompare(b.message || b.description || ''),
            render: (text, record) => {
                const content = text || record.description || '';
                if (record.type === 'multi-step') {
                    return <span style={{ color: '#8c8c8c', fontStyle: 'italic' }}>{record.stepCount} steps — {content.length > 80 ? `${content.substring(0, 80)}...` : content}</span>;
                }
                return content && content.length > 100 ? `${content.substring(0, 100)}...` : content;
            },
        },
        {
            title: 'Category',
            dataIndex: 'category',
            sorter: (a, b) => (a.category || '').localeCompare(b.category || ''),
            render: (text) => (
                <Tag style={{ margin: 0, borderRadius: 999 }} color={text ? undefined : 'default'}>
                    {text || 'Uncategorized'}
                </Tag>
            ),
        },
        {
            title: 'Owner',
            dataIndex: ['owner', 'name'],
            width: 190,
            ellipsis: true,
            sorter: (a, b) => (a.owner?.name || '').localeCompare(b.owner?.name || ''),
            render: (text, record) => {
                return (
                    <Space size={8}>
                        <UserAvatar
                            name={record.owner?.name || 'Unknown'}
                            src={record.owner?.avatar_url}
                            userId={record.owner?.id}
                            size={24}
                        />
                        <span>{text || 'Unknown'}</span>
                        {record.privacy === 'private' && (
                            <LockOutlined style={{ fontSize: 12, color: '#9ca3af' }} title="Private" />
                        )}
                    </Space>
                );
            },
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 280,
            fixed: 'right',
            render: (_, record) => {
                const isOwner = record.owner?.id === userData.id;
                const isDefault = defaultTemplateIds.includes(record.id);
                const canModify = (isOwner || isAdmin) && !isDefault;

                const duplicateButton = (
                    <Button
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={() => onDuplicateTemplate(record)}
                    >
                        Duplicate
                    </Button>
                );

                if (!canModify) return duplicateButton;

                if (record.type === 'multi-step') {
                    return (
                        <Space size={6}>
                            {duplicateButton}
                            <Button
                                size="small"
                                icon={<EditOutlined />}
                                onClick={() => onEditSequence(record)}
                            >
                                Edit
                            </Button>
                            <Button
                                size="small"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={async () => {
                                    const count = await onFetchEnrollmentCount(record.id);
                                    Modal.confirm({
                                        title: 'Delete Sequence',
                                        content: count > 0
                                            ? `This sequence has ${count} enrolled contact(s). Deleting it will unenroll all contacts and cancel their pending scheduled messages. This action cannot be undone.`
                                            : 'Are you sure you want to delete this sequence? This cannot be undone.',
                                        okText: 'Delete',
                                        okType: 'danger',
                                        onOk: () => onDeleteTemplate(record),
                                    });
                                }}
                            >
                                Delete
                            </Button>
                        </Space>
                    );
                }

                return (
                    <Space size={6}>
                        {duplicateButton}
                        <Button
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => onEditTemplate(record)}
                        >
                            Edit
                        </Button>
                        <Popconfirm
                            title="Delete this template?"
                            onConfirm={() => onDeleteTemplate(record)}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Button size="small" danger icon={<DeleteOutlined />}>
                                Delete
                            </Button>
                        </Popconfirm>
                    </Space>
                );
            },
        },
    ];

    return (
        <div style={{ padding: '8 0px' }}>
            {/* Toolbar */}
            <div className="gh-compact-toolbar" style={{ marginBottom: 16 }}>
                <Row gutter={[16, 16]} align="middle">
                    <Col xs={24} md={10}>
                        <Input
                            placeholder="Search templates..."
                            prefix={<SearchOutlined />}
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            allowClear
                        />
                    </Col>
                </Row>
            </div>

            {/* Table */}
            <Table
                className="gh-themed-table"
                dataSource={filteredItems}
                columns={columns}
                rowKey="id"
                loading={loading}
                size="small"
                scroll={{ x: 1000 }}
                pagination={{
                    pageSize: 20,
                    showSizeChanger: false,
                    showQuickJumper: false,
                    showTotal: (total, range) => {
                        return filteredItems.length === items.length
                            ? `${range[0]}-${range[1]} of ${total} templates`
                            : `${range[0]}-${range[1]} of ${filteredItems.length} filtered templates (${items.length} total)`;
                    },
                }}
            />
        </div>
    );
};

export default MyTemplates;
